import { useResizeObserver } from '@vueuse/core'
import { computed, onBeforeMount, ref, watch, type ComponentPublicInstance, type Ref } from 'vue'

const DEFAULT_HEIGHT_FALLBACK = 150

/**
 * Can be used with vertically resizable elements, like TextArea or PromptEditor.
 *
 * It will persist the height to localStorage using the provided key, and will
 * read the height from localStorage if the key is provided.
 *
 * If no key is provided, it will not store anything.
 *
 * Default height (when there is nothing in storage), can be specified, or a global default is used.
 */
export const usePersistedResizableHeight = (
  storageKey: Ref<string | undefined>,
  elRef: Ref<HTMLElement | ComponentPublicInstance | null>,
  defaultHeight: number = DEFAULT_HEIGHT_FALLBACK,
) => {
  const height = ref(defaultHeight)

  onBeforeMount(() => {
    if (storageKey.value) {
      height.value = Number(localStorage.getItem(storageKey.value) ?? defaultHeight)
    }
  })

  watch(storageKey, (newKey, oldKey) => {
    if (newKey && !oldKey) {
      height.value = Number(localStorage.getItem(newKey) ?? defaultHeight)
    }
  })

  const heightProxy = computed({
    get: () => height.value,
    set: (value) => {
      if (storageKey.value) {
        localStorage.setItem(storageKey.value, value.toString())
      }
      height.value = value
    },
  })

  const cssHeight = computed<`${number}px` | ''>(() => {
    if (!storageKey.value) return ''
    return `${heightProxy.value}px`
  })

  useResizeObserver(elRef, () => {
    if (!elRef.value) return
    if (elRef.value instanceof HTMLElement) {
      heightProxy.value = elRef.value.getBoundingClientRect().height
    } else {
      heightProxy.value = elRef.value?.$el.getBoundingClientRect().height
    }
  })

  return cssHeight
}
